import { useEffect, useState } from 'react';
import { EnterpriseWorkspaceNav } from '../components/enterprise/EnterpriseWorkspaceNav';
import { useEnterpriseOrg } from '../context/EnterpriseOrgContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useEnterpriseWorkspaceRole } from '../hooks/use-enterprise-workspace-role';
import { useAuth } from '../lib/auth-context';
import { fetchEnterpriseWorkspacePolicy, saveEnterpriseWorkspacePolicy } from '../lib/enterprise-api';
import { useEnterpriseCapabilities } from '../hooks/use-enterprise-capabilities';

export default function EnterpriseWorkspaceSettings() {
  const { activeOrgId, activeMembership } = useEnterpriseOrg();
  const { accessToken } = useAuth();
  const { role, setRole } = useEnterpriseWorkspaceRole();
  const capabilities = useEnterpriseCapabilities();
  const canWritePolicyCapability = capabilities.hasCapability('workspace.policy.write');
  const [digestOptIn, setDigestOptIn] = useState(true);
  const [alertThreshold, setAlertThreshold] = useState('28');
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);
  const [canEditPolicy, setCanEditPolicy] = useState(false);
  const [slaHours, setSlaHours] = useState('24');
  const [segmentTrendDeltaMin, setSegmentTrendDeltaMin] = useState('4');
  const [segmentHighPctMin, setSegmentHighPctMin] = useState('28');

  useEffect(() => {
    let cancelled = false;
    if (!accessToken || !activeOrgId) {
      setCanEditPolicy(false);
      return;
    }
    setPolicyLoading(true);
    void fetchEnterpriseWorkspacePolicy(accessToken, activeOrgId)
      .then((res) => {
        if (cancelled) return;
        if (!res.success || !res.policy) {
          setCanEditPolicy(false);
          return;
        }
        setCanEditPolicy(Boolean(res.canEdit) || canWritePolicyCapability);
        setSlaHours(String(res.policy.responseSlaHours));
        setSegmentTrendDeltaMin(String(res.policy.segmentAlertPolicy.trendDeltaMin));
        setSegmentHighPctMin(String(res.policy.segmentAlertPolicy.highTensionPctMin));
      })
      .finally(() => {
        if (!cancelled) setPolicyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, activeOrgId, canWritePolicyCapability]);

  const handleSavePolicy = async () => {
    if (!accessToken || !activeOrgId) {
      toast.error('Select an org and sign in first.');
      return;
    }
    setPolicySaving(true);
    const responseSlaHours = Number(slaHours);
    const trendDeltaMin = Number(segmentTrendDeltaMin);
    const highTensionPctMin = Number(segmentHighPctMin);
    const res = await saveEnterpriseWorkspacePolicy(accessToken, {
      organizationId: activeOrgId,
      responseSlaHours,
      segmentAlertPolicy: {
        trendDeltaMin: Number.isFinite(trendDeltaMin) ? trendDeltaMin : undefined,
        highTensionPctMin: Number.isFinite(highTensionPctMin) ? highTensionPctMin : undefined,
      },
    });
    setPolicySaving(false);
    if (!res.success || !res.policy) {
      toast.error(res.error || 'Could not save enterprise policy');
      return;
    }
    setSlaHours(String(res.policy.responseSlaHours));
    setSegmentTrendDeltaMin(String(res.policy.segmentAlertPolicy.trendDeltaMin));
    setSegmentHighPctMin(String(res.policy.segmentAlertPolicy.highTensionPctMin));
    toast.success('Enterprise policy saved.');
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#020617_0%,#0b1220_100%)]">
      <EnterpriseWorkspaceNav />
      <div className="mx-auto max-w-5xl space-y-4 px-4 py-6">
        <h1 className="text-2xl font-bold text-slate-100">Enterprise workspace settings</h1>
        <Card className="border-slate-700 bg-slate-900/80 p-4 text-sm text-slate-300">
          <p>Org: {activeMembership?.organizations?.name || 'None selected'} {activeOrgId ? `(${activeOrgId.slice(0, 8)}...)` : ''}</p>
          {capabilities.role ? <p className="mt-1 text-xs text-slate-400">RBAC role: {capabilities.role}</p> : null}
          {capabilities.error ? <p className="mt-1 text-xs text-amber-300">RBAC warning: {capabilities.error}</p> : null}
        </Card>

        <Card className="space-y-3 border-slate-700 bg-slate-900/80 p-4">
          <div className="space-y-1">
            <label className="text-sm text-slate-300">Workspace role profile</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'executive' | 'hr' | 'manager' | 'partner')}
              className="w-full max-w-xs rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100"
            >
              <option value="executive">executive</option>
              <option value="hr">hr</option>
              <option value="manager">manager</option>
              <option value="partner">partner</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={digestOptIn} onChange={(e) => setDigestOptIn(e.target.checked)} />
            Receive workspace digest summaries
          </label>
          <div className="space-y-1">
            <label className="text-sm text-slate-300">High tension alert threshold (%)</label>
            <input
              value={alertThreshold}
              onChange={(e) => setAlertThreshold(e.target.value)}
              className="w-full max-w-xs rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100"
            />
          </div>
          <Button
            className="bg-blue-600 text-white"
            onClick={() => toast.success('Workspace settings saved (frontend state demo).')}
          >
            Save settings
          </Button>
          <div className="mt-4 border-t border-slate-700 pt-4">
            <p className="text-sm font-semibold text-slate-100">Queue policy controls</p>
            <p className="mt-1 text-xs text-slate-400">
              Govern intervention SLA and thresholding for enterprise workspace signals.
            </p>
            {policyLoading ? <p className="mt-2 text-xs text-slate-400">Loading org policy…</p> : null}
            {!policyLoading && !canEditPolicy ? (
              <p className="mt-2 text-xs text-amber-300">You can view policy telemetry, but only owner/admin can edit controls.</p>
            ) : null}
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <label className="space-y-1 text-sm text-slate-300">
                <span className="text-xs uppercase tracking-wide text-slate-400">Response SLA (hours)</span>
                <input
                  value={slaHours}
                  onChange={(e) => setSlaHours(e.target.value)}
                  disabled={!canEditPolicy}
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100 disabled:opacity-60"
                />
              </label>
              <label className="space-y-1 text-sm text-slate-300">
                <span className="text-xs uppercase tracking-wide text-slate-400">Trend delta threshold</span>
                <input
                  value={segmentTrendDeltaMin}
                  onChange={(e) => setSegmentTrendDeltaMin(e.target.value)}
                  disabled={!canEditPolicy}
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100 disabled:opacity-60"
                />
              </label>
              <label className="space-y-1 text-sm text-slate-300">
                <span className="text-xs uppercase tracking-wide text-slate-400">High tension % threshold</span>
                <input
                  value={segmentHighPctMin}
                  onChange={(e) => setSegmentHighPctMin(e.target.value)}
                  disabled={!canEditPolicy}
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100 disabled:opacity-60"
                />
              </label>
            </div>
            <Button
              className="mt-3 bg-violet-600 text-white disabled:opacity-60"
              disabled={!canEditPolicy || policySaving}
              onClick={() => void handleSavePolicy()}
            >
              {policySaving ? 'Saving policy…' : 'Save queue policy'}
            </Button>
          </div>
          <Button
            variant="outline"
            className="border-slate-600 bg-slate-900 text-slate-100"
            onClick={() => {
              if (!activeOrgId) return;
              const raw = localStorage.getItem('tension_enterprise_workspace_checkins_v1');
              if (!raw) {
                toast.message('No local workspace data to clear.');
                return;
              }
              try {
                const rows = JSON.parse(raw) as Array<{ organizationId?: string }>;
                const next = rows.filter((row) => row.organizationId !== activeOrgId);
                localStorage.setItem('tension_enterprise_workspace_checkins_v1', JSON.stringify(next));
                toast.success('Cleared local workspace history for active org.');
              } catch {
                toast.error('Could not clear local history.');
              }
            }}
          >
            Clear local workspace history for this org
          </Button>
        </Card>
      </div>
    </div>
  );
}

